<template>
  <div class="model-portfolio-container">
    <!-- Full Screen Loading State for Re-run -->
    <div v-if="isRerunningFullScreen" class="loading-state">
      <div class="loading-content">
        <i class="pi pi-spinner pi-spin loading-icon"></i>
        <h2>Re-running Your Model Portfolio Allocation</h2>
        <p>Based on your revised goals, we are creating your personalized investment portfolio...</p>
      </div>
    </div>

    <!-- Loading State for Risk Assessment -->
    <div v-else-if="isRiskAssessmentLoading" class="loading-state">
      <div class="loading-content">
        <i class="pi pi-spinner pi-spin loading-icon"></i>
        <h2 v-if="isRerunning">Re-running Your Model Portfolio Allocation</h2>
        <h2 v-else>Completing Your Risk Assessment</h2>
        <p v-if="isRerunning">Based on your revised goals, we are creating your personalized investment portfolio...</p>
        <p v-else>We're analyzing your responses to create your personalized investment portfolio...</p>
      </div>
    </div>

    <!-- Risk Assessment Questionnaire -->
    <div v-else-if="!riskAssessmentData && !hasExistingPortfolio" class="questionnaire-container">
      <!-- Only show intro section when NOT in dashboard mode -->
      <div v-if="!isDashboardMode" class="intro-section">
        <h2>Welcome {{ user?.firstName || user?.username }}!</h2>
        <p class="intro-text">
          To provide you with personalized investment recommendations, we need to understand your risk tolerance and investment preferences.
          Please complete this quick assessment.
        </p>
        <div class="user-info-card">
          <div class="user-details">
            <div class="detail-row">
              <span class="label">Name:</span>
              <span class="value">{{ user?.firstName }} {{ user?.lastName }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Email:</span>
              <span class="value">{{ user?.email }}</span>
            </div>
            <div class="detail-row" v-if="customerData?.dob">
              <span class="label">Date of Birth:</span>
              <span class="value">{{ formatDate(customerData.dob) }}</span>
            </div>
            <div class="detail-row" v-if="customerData?.address">
              <span class="label">Address:</span>
              <span class="value">{{ customerData.address }}</span>
            </div>
          </div>
        </div>
      </div>

      <RiskQuestionnaire
        :onSubmit="handleRiskAssessmentComplete"
        :is-dashboard-mode="isDashboardMode"
      />
    </div>

    <!-- Model Portfolio Results -->
    <div v-else-if="!isRerunningFullScreen" class="portfolio-results">
      <!-- Only show user profile summary when NOT in dashboard mode -->
      <div v-if="!isDashboardMode" class="user-profile-summary">
        <div class="profile-card">
          <div class="profile-header">
            <h3>{{ user?.firstName || user?.username }}'s Investment Profile</h3>
          </div>
          <div class="profile-details">
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Name</span>
                <span class="detail-value">{{ user?.firstName }} {{ user?.lastName }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Email</span>
                <span class="detail-value">{{ user?.email }}</span>
              </div>
              <div class="detail-item" v-if="customerData?.dob">
                <span class="detail-label">Age</span>
                <span class="detail-value">{{ calculateAge(customerData.dob) }} years</span>
              </div>
              <div class="detail-item" v-if="customerData?.address">
                <span class="detail-label">Location</span>
                <span class="detail-value">{{ getLocationFromAddress(customerData.address) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Portfolio Allocation -->
      <div class="portfolio-allocation">
        <!-- Chart and Analytics Section -->
        <div class="allocation-display">
          <!-- Two Column Layout -->
          <div class="chart-analytics-container">
            <!-- Left Column - Chart Section -->
            <div class="chart-section">
              <div class="chart-container">
                <h3 class="chart-title">Recommended Model Portfolio</h3>

                <!-- Chart Loading State -->
                <div v-if="!normalizedAllocations || normalizedAllocations.length === 0" class="chart-loading">
                  <i class="pi pi-spinner pi-spin chart-loading-icon"></i>
                  <p class="chart-loading-text">Loading asset class distribution chart...</p>
                </div>

                <!-- Chart Display -->
                <div v-else>
                  <Chart
                    type="doughnut"
                    :data="chartData"
                    :options="chartOptions"
                    class="portfolio-chart"
                  />
                  <!-- Custom Legend -->
                  <div class="custom-legend">
                    <div class="legend-grid">
                      <div
                        v-for="(allocation, index) in normalizedAllocations"
                        :key="allocation.name"
                        class="legend-item"
                      >
                        <div class="legend-dot" :style="{ backgroundColor: chartColors[index] }"></div>
                        <div class="legend-text">
                          <span class="legend-label">{{ allocation.name }}: </span>
                          <span class="legend-percentage">{{ allocation.percentage }}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column - Investment Parameters Only -->
            <div class="parameters-section">
              <h3 class="section-title">Investment Parameters</h3>
              <div class="parameters-list">
                <!-- Initial Investment -->
                <div class="parameter-row">
                  <label class="parameter-label">Initial Investment:</label>
                  <div class="parameter-value-group">
                    <InputNumber
                      v-model="editableParams.initialInvestment"
                      :disabled="!isEditingParams.initialInvestment"
                      :class="{ 'editing-field': isEditingParams.initialInvestment }"
                      mode="currency"
                      currency="INR"
                      locale="en-IN"
                      class="parameter-input"
                      :min="100000"
                      :step="50000"
                      @keyup.enter="finishEditing('initialInvestment')"
                    />
                    <Button
                      icon="pi pi-pencil"
                      class="p-button-text p-button-sm edit-btn"
                      @click="toggleEditMode('initialInvestment')"
                      v-tooltip="'Edit Initial Investment'"
                    />
                  </div>
                </div>

                <!-- Goal Return -->
                <div class="parameter-row">
                  <label class="parameter-label">Goal Return (Annual):</label>
                  <div class="parameter-value-group">
                    <InputNumber
                      v-model="editableParams.goalReturn"
                      :disabled="!isEditingParams.goalReturn"
                      :class="{ 'editing-field': isEditingParams.goalReturn }"
                      suffix="%"
                      class="parameter-input"
                      :min="5"
                      :max="25"
                      :step="0.1"
                      :maxFractionDigits="1"
                      @keyup.enter="finishEditing('goalReturn')"
                    />
                    <Button
                      icon="pi pi-pencil"
                      class="p-button-text p-button-sm edit-btn"
                      @click="toggleEditMode('goalReturn')"
                      v-tooltip="'Edit Goal Return'"
                    />
                  </div>
                </div>

                <!-- Investment Horizon -->
                <div class="parameter-row">
                  <label class="parameter-label">Investment Horizon:</label>
                  <div class="parameter-value-group">
                    <InputNumber
                      v-model="editableParams.investmentHorizon"
                      :disabled="!isEditingParams.investmentHorizon"
                      :class="{ 'editing-field': isEditingParams.investmentHorizon }"
                      suffix=" Years"
                      class="parameter-input"
                      :min="1"
                      :max="20"
                      :step="1"
                      @keyup.enter="finishEditing('investmentHorizon')"
                    />
                    <Button
                      icon="pi pi-pencil"
                      class="p-button-text p-button-sm edit-btn"
                      @click="toggleEditMode('investmentHorizon')"
                      v-tooltip="'Edit Investment Horizon'"
                    />
                  </div>
                </div>
              </div>

              <!-- Re-run Button -->
              <div class="rerun-section">
                <ANButton
                  label="Re-run Asset Allocation"
                  icon="pi pi-refresh"
                  variant="primary"
                  :disabled="!hasParameterChanges || isRiskAssessmentLoading"
                  :loading="isRiskAssessmentLoading"
                  @click="rerunAssetAllocation"
                  class="rerun-btn"
                />
              </div>
            </div>
          </div>

          <!-- Portfolio Analytics - Full Width Row Below -->
          <div v-if="isLoadingAnalytics" class="analytics-loading">
            <div class="loading-content">
              <i class="pi pi-spinner pi-spin loading-icon"></i>
              <h3>Loading Portfolio Analytics...</h3>
              <p>We're fetching your portfolio performance data and projections...</p>
            </div>
          </div>

          <div v-else-if="portfolioAnalytics" class="analytics-row">
            <h3 class="analytics-title">Analytics of Your Model Portfolio</h3>

            <!-- Key Metrics Cards -->
            <div class="metrics-container">
              <!-- Achieved Return -->
              <div class="metric-card achieved-return">
                <div class="metric-icon">
                  <i class="pi pi-trending-up"></i>
                </div>
                <div class="metric-content">
                  <div class="metric-label">Achieved Return (CAGR)</div>
                  <div class="metric-value">{{ portfolioAnalytics.returns_analysis?.nominal_annual_return || 0 }}%</div>
                </div>
              </div>

              <!-- Goal Achievement -->
              <div class="metric-card">
                <div class="metric-icon">
                  <i class="pi pi-trophy"></i>
                </div>
                <div class="metric-content">
                  <div class="metric-label">Goal Achievement ({{ editableParams.goalReturn }}% Target)</div>
                  <div class="metric-value">{{ getGoalAchievementPercentage() }}%</div>
                </div>
              </div>

              <!-- Sharpe Ratio -->
              <div class="metric-card">
                <div class="metric-icon">
                  <i class="pi pi-shield" v-tooltip="'Sharpe Ratio measures risk-adjusted returns. Values >1 are good, >2 are excellent. Higher values indicate better returns per unit of risk taken.'"></i>
                </div>
                <div class="metric-content">
                  <div class="metric-label">Sharpe Ratio</div>
                  <div class="metric-value">{{ (portfolioAnalytics.sharpe_ratio || 0).toFixed(2) }}</div>
                </div>
              </div>

              <!-- Alpha (Excess Return) -->
              <div class="metric-card">
                <div class="metric-icon">
                  <i class="pi pi-arrow-up" v-tooltip="'Alpha represents excess return over the risk-free rate (Bank FD). Positive alpha indicates outperformance of safe investments.'"></i>
                </div>
                <div class="metric-content">
                  <div class="metric-label">Alpha over Bank FD</div>
                  <div class="metric-value">+{{ ((portfolioAnalytics.excess_return || 0) * 100).toFixed(2) }}%</div>
                </div>
              </div>

              <!-- Volatility -->
              <div class="metric-card">
                <div class="metric-icon">
                  <i class="pi pi-chart-bar" v-tooltip="'Annual volatility measures how much the portfolio value fluctuates. Lower volatility means more stable returns but potentially lower growth.'"></i>
                </div>
                <div class="metric-content">
                  <div class="metric-label">Annual Volatility</div>
                  <div class="metric-value">{{ ((portfolioAnalytics.risk_metrics?.annual_volatility || 0) * 100).toFixed(1) }}%</div>
                </div>
              </div>

              <!-- Risk Free Rate -->
              <div class="metric-card">
                <div class="metric-icon">
                  <i class="pi pi-bookmark" v-tooltip="'Bank FD rate is used as risk-free benchmark because it represents guaranteed returns with capital protection in India. Unlike G-Sec bonds, Bank FDs are widely understood and accessible to all investors.'"></i>
                </div>
                <div class="metric-content">
                  <div class="metric-label">Bank FD Rate (Risk-Free)</div>
                  <div class="metric-value">{{ ((portfolioAnalytics.risk_free_rate || 0) * 100).toFixed(2) }}%</div>
                </div>
              </div>
            </div>

            <!-- Projections and Risk in Sub-grid -->
            <div class="analytics-subgrid">
              <!-- Projections -->
              <div class="projections-section">
                <h4 class="subsection-title">Investment Projections</h4>
                <div class="projections-grid">
                  <div
                    v-for="(projection, period) in getProjections()"
                    :key="period"
                    class="projection-card"
                  >
                    <div class="projection-period">{{ formatPeriod(period) }}</div>
                    <div class="projection-amount">₹{{ formatCurrency(projection.nominal_value) }}</div>
                    <div class="projection-gain">+₹{{ formatCurrency(projection.nominal_gain) }}</div>
                  </div>
                </div>
              </div>

              <!-- Risk Assessment -->
              <div class="risk-assessment">
                <h4 class="subsection-title">Risk Assessment</h4>
                <div class="risk-items">
                  <div class="risk-item">
                    <span class="risk-label">
                      Max Drawdown:
                      <i class="pi pi-info-circle risk-info" v-tooltip="'Maximum expected portfolio decline from peak value during adverse market conditions. Indicates worst-case scenario loss during market downturns.'"></i>
                    </span>
                    <span class="risk-value">{{ ((portfolioAnalytics.risk_metrics?.downside_protection?.expected_maximum_drawdown || 0) * 100).toFixed(1) }}%</span>
                  </div>
                  <div class="risk-item">
                    <span class="risk-label">Probability of Loss:</span>
                    <span class="risk-value">{{ (portfolioAnalytics.risk_metrics?.downside_protection?.probability_of_loss || 0).toFixed(1) }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Asset Cards Grid - Below Two Columns -->
          <div class="allocation-grid">
            <div
              v-for="allocation in normalizedAllocations"
              :key="allocation.name"
              class="allocation-card"
            >
              <div class="allocation-header-card">
                <div class="allocation-name-risk">
                  <h4>{{ allocation.name }}</h4>
                  <span class="risk-badge" :class="getRiskBadgeClass(allocation.risk)">
                    RISK: {{ allocation.risk.toUpperCase() }}
                  </span>
                </div>
              </div>
              <div class="allocation-percentage-large">
                {{ allocation.percentage }}%
              </div>
              <div class="allocation-description">
                {{ allocation.description }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <ANButton
          label="Investment Vehicles"
          variant="secondary"
          @click="viewInvestmentVehicles"
          class="action-btn"
        />
        <ANButton
          label="Go to Dashboard"
          variant="primary"
          @click="goToDashboard"
          class="action-btn"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useUserStore } from '@/store/user-store'
import Chart from 'primevue/chart'
import InputNumber from 'primevue/inputnumber'
import Button from 'primevue/button'
import ANButton from '@/components/common/AnButton.vue'
import RiskQuestionnaire from '@/components/landing/RiskQuestionnaire.vue'

const store = useUserStore()

// Props for section management (similar to sector analytics)
const props = defineProps({
  setSection: {
    type: Function,
    default: null
  }
})

// Reactive data
const isRiskAssessmentLoading = computed(() => store.isRiskAssessmentLoading)
const riskAssessmentData = computed(() => store.riskAssessmentData)
const user = computed(() => store.getuserData)
const isDashboardMode = computed(() => {
  // When accessed from dashboard (via userTab routing), it's dashboard mode
  // When accessed from landing page, it's not dashboard mode
  return store.userTab === 'MyModelPortfolio'
})

const hasExistingPortfolio = computed(() => {
  // Check if user already has portfolio allocation from database
  const portfolioTargetAlloc = store.dashboardData?.portfolioTargetAlloc
  return portfolioTargetAlloc && Array.isArray(portfolioTargetAlloc) && portfolioTargetAlloc.length > 0
})

const customerData = computed(() => {
  // Extract customer data from dashboard or other sources
  return {
    firstName: user.value?.firstName,
    lastName: user.value?.lastName,
    dob: store.dashboardData?.dateOfBirth,
    address: store.dashboardData?.address
  }
})

// Chart data and options
const chartData = ref({})
const chartOptions = ref({})
const normalizedAllocations = ref([])
const chartColors = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
  '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
]

// Portfolio analytics data
const portfolioAnalytics = ref(null)
const inputParameters = ref(null)
const isLoadingAnalytics = ref(true) // Loading state for analytics
const isRerunning = ref(false) // Track if we're re-running allocation vs initial assessment
const isRerunningFullScreen = ref(false) // Full screen loading during re-run

// Editable parameters
const editableParams = ref({
  initialInvestment: 1000000, // Default Rs. 10 lakh
  goalReturn: 12, // Default 12%
  investmentHorizon: 5 // Default 5 years
})

const originalParams = ref({
  initialInvestment: 1000000,
  goalReturn: 12,
  investmentHorizon: 5
})

const isEditingParams = ref({
  initialInvestment: false,
  goalReturn: false,
  investmentHorizon: false
})

// Mapping between database asset class names and display asset mapping keys
const assetClassMapping = {
  'Large Cap': 'Large-cap Eq.',
  'Mid Cap': 'Mid-cap Eq.',
  'Small Cap': 'Small-cap Eq.',
  'Fixed Deposit': 'Fixed Deposits',
  'Gold': 'Gold-SGB',
  'Flexi-cap Mutual Fund': 'Flexicap MF',
  'Debt Mutual Fund': 'Debt MF',
  'Real Estate': 'Real Estate'
}

// Asset mapping from API keys to display names
const assetMapping = {
  'Large-cap Eq.': {
    name: 'Large-Cap Equity',
    risk: 'Low',
    description: 'Top 100 companies by market cap per SEBI, like Reliance, TCS, and Infosys offering stability.'
  },
  'Mid-cap Eq.': {
    name: 'Mid-Cap Equity',
    risk: 'Medium',
    description: 'Companies ranked 101 to 250 by market capitalisation, local and global leaders with higher growth potential.'
  },
  'Small-cap Eq.': {
    name: 'Small-Cap Equity',
    risk: 'High',
    description: 'Companies ranked below 250 by market capitalisation with high growth potential but higher volatility.'
  },
  'Fixed Deposits': {
    name: 'Fixed Deposits',
    risk: 'Low',
    description: 'Bank FDs and similar fixed income instruments offering capital protection with moderate returns.'
  },
  'Gold-SGB': {
    name: 'Gold (SGB)',
    risk: 'Medium',
    description: 'Sovereign Gold Bonds offering exposure to gold prices with additional interest income.'
  },
  'Flexicap MF': {
    name: 'Flexi-Cap Mutual Funds',
    risk: 'Medium',
    description: 'Diversified equity funds investing across market caps with flexible allocation strategies.'
  },
  'Debt MF': {
    name: 'Debt Mutual Funds',
    risk: 'Low',
    description: 'Fixed income mutual funds investing in bonds, government securities, and corporate debt.'
  },
  'Real Estate': {
    name: 'Real Estate',
    risk: 'Medium',
    description: 'REITs and real estate investments offering property exposure with potential for steady income.'
  }
}

// Methods
const handleRiskAssessmentComplete = async (answersData) => {
  try {
    // Transform answers into the format expected by the API
    const answers = []
    let totalScore = 0

    // Process each answer and calculate total score
    Object.entries(answersData).forEach(([questionId, selectedOption]) => {
      answers.push({
        questionId,
        selectedValue: selectedOption.value,
        score: selectedOption.score
      })
      totalScore += selectedOption.score
    })

    // Calculate risk aversion coefficient based on total score ranges (like RiskProfilePage.vue)
    // Using the same logic as the landing page risk assessment
    let riskAversionCoefficient
    if (totalScore >= 6 && totalScore <= 10) {
      riskAversionCoefficient = 6  // Conservative
    } else if (totalScore >= 11 && totalScore <= 15) {
      riskAversionCoefficient = 4  // Moderate
    } else if (totalScore >= 16 && totalScore <= 20) {
      riskAversionCoefficient = 2  // Aggressive
    } else {
      // Fallback for edge cases
      riskAversionCoefficient = totalScore <= 10 ? 6 : totalScore <= 15 ? 4 : 2
    }

    console.log('Total Score:', totalScore)
    console.log('Risk Aversion Coefficient:', riskAversionCoefficient)

    const assessmentPayload = {
      answers,
      totalScore,
      riskAversionCoefficient,
      customerId: store.dashboardData?.customerId,
      email: store.user?.email
    }

    console.log('Submitting risk assessment:', assessmentPayload)

    // Submit the risk assessment
    const result = await store.getRiskAssessment(assessmentPayload)

    console.log('Risk assessment completed:', result)

    // Reload dashboard data to get updated portfolioTargetAlloc
    await store.loadDashboardData(store.user.email)

    // Now load portfolio analytics since we have completed risk assessment
    await loadPortfolioAnalytics()

    // Transform the data for display
    transformAssessmentData()

    // Show success message
    store.fireAlert('Success', 'Risk assessment completed! Your personalized portfolio has been created.')

    // After a few seconds, redirect to Dashboard
    //setTimeout(() => {
      //store.setTabView('Dashboard')
    //}, 3000)

  } catch (error) {
    console.error('Risk assessment submission failed:', error)
    store.fireAlert('Error', 'Failed to complete risk assessment. Please try again.')
  }
}

const transformAssessmentData = () => {
  if (!riskAssessmentData.value) return

  // Extract allocations and transform for display
  const allocations = []
  const chartLabels = []
  const chartDataValues = []

  Object.entries(riskAssessmentData.value).forEach(([key, value]) => {
    if (typeof value === 'number' && assetMapping[key]) {
      const mapping = assetMapping[key]
      allocations.push({
        name: mapping.name,
        risk: mapping.risk,
        description: mapping.description,
        percentage: value.toFixed(1),
        originalKey: key
      })

      chartLabels.push(mapping.name)
      chartDataValues.push(value)
    }
  })

  // Sort by percentage descending
  allocations.sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))

  normalizedAllocations.value = allocations

  // Setup chart data
  chartData.value = {
    labels: chartLabels,
    datasets: [{
      data: chartDataValues,
      backgroundColor: chartColors.slice(0, chartLabels.length),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  }

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.parsed}%`
          }
        }
      }
    },
    layout: {
      padding: 0
    }
  }
}

// Transform existing portfolio data from dashboardData.portfolioTargetAlloc
const transformExistingPortfolioData = () => {
  const portfolioTargetAlloc = store.dashboardData?.portfolioTargetAlloc
  if (!portfolioTargetAlloc || !Array.isArray(portfolioTargetAlloc) || portfolioTargetAlloc.length === 0) {
    return
  }

  const allocations = []
  const chartLabels = []
  const chartDataValues = []
  const chartColors = [
    '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
    '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF'
  ]

  portfolioTargetAlloc.forEach((allocation) => {
    const { assetClassName, targetPct } = allocation
    const mappedKey = assetClassMapping[assetClassName]

    if (mappedKey && assetMapping[mappedKey]) {
      const mapping = assetMapping[mappedKey]
      allocations.push({
        name: mapping.name,
        risk: mapping.risk,
        description: mapping.description,
        percentage: targetPct.toFixed(1),
        originalKey: mappedKey,
        assetClassName
      })

      chartLabels.push(mapping.name)
      chartDataValues.push(targetPct)
    }
  })

  // Sort by percentage descending
  allocations.sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))

  normalizedAllocations.value = allocations

  // Setup chart data
  chartData.value = {
    labels: chartLabels,
    datasets: [{
      data: chartDataValues,
      backgroundColor: chartColors.slice(0, chartLabels.length),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  }

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.parsed}%`
          }
        }
      }
    },
    layout: {
      padding: 0
    }
  }
}

// Utility methods
const getRiskBadgeClass = (risk) => {
  return `risk-badge-${risk.toLowerCase()}`
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const calculateAge = (dob) => {
  if (!dob) return ''
  const birthDate = new Date(dob)
  const today = new Date()
  const age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1
  }
  return age
}

const getLocationFromAddress = (address) => {
  if (!address) return ''
  // Extract city/state from address - simple implementation
  const parts = address.split(',')
  return parts.length > 1 ? parts[parts.length - 2].trim() : address
}

const viewInvestmentVehicles = () => {
  // Use setSection if available (internal navigation), otherwise fall back to store navigation
  if (props.setSection) {
    props.setSection(2) // Navigate to investment vehicles section
  } else {
    store.setTabView('ModelPortfolioInvestmentVehicles')
  }
}

const goToDashboard = () => {
  store.setTabView('Dashboard')
}

// Portfolio analytics methods
const loadPortfolioAnalytics = async () => {
  try {
    // Only load analytics if user has completed risk assessment or has existing portfolio
    if (!riskAssessmentData.value && !hasExistingPortfolio.value) {
      console.log('Skipping analytics load - no risk assessment or existing portfolio data')
      isLoadingAnalytics.value = false
      portfolioAnalytics.value = null
      inputParameters.value = null
      return
    }

    isLoadingAnalytics.value = true
    const debugInfo = await store.getAssetAllocationDebugInfo()
    if (debugInfo && debugInfo.portfolio_analytics) {
      portfolioAnalytics.value = debugInfo.portfolio_analytics
      inputParameters.value = debugInfo.input_parameters

      // Set editable parameters from API data if available
      if (debugInfo.input_parameters) {
        // Handle goal return conversion - ensure it's always in percentage format (e.g., 13, not 0.13)
        let goalReturnValue = 12 // default

        if (debugInfo.input_parameters.goal_return_percentage) {
          // If goal_return_percentage is provided, check if it's in decimal or percentage format
          const goalReturnFromAPI = debugInfo.input_parameters.goal_return_percentage
          goalReturnValue = goalReturnFromAPI < 1 ? goalReturnFromAPI * 100 : goalReturnFromAPI
        } else if (debugInfo.portfolio_analytics?.goal_achievement?.target_annual_return) {
          // Fallback to portfolio analytics (likely in decimal format)
          goalReturnValue = debugInfo.portfolio_analytics.goal_achievement.target_annual_return * 100
        }

        editableParams.value = {
          initialInvestment: debugInfo.input_parameters.initial_investment || 1000000,
          goalReturn: goalReturnValue,
          investmentHorizon: debugInfo.input_parameters.investment_horizon || 5
        }

        // Update original params for comparison
        originalParams.value = { ...editableParams.value }
      }
    }
  } catch (error) {
    console.error('Failed to load portfolio analytics:', error)
    // Use default values if API fails
    portfolioAnalytics.value = null
    inputParameters.value = null
  } finally {
    isLoadingAnalytics.value = false
  }
}

const getGoalAchievementPercentage = () => {
  if (!portfolioAnalytics.value?.goal_achievement) return 0

  const goalAchievement = portfolioAnalytics.value.goal_achievement
  const monteCarlo = goalAchievement.monte_carlo_probability || 0
  const statistical = goalAchievement.statistical_details?.probability_percentage || 0

  // Return the higher of the two probabilities
  return Math.max(monteCarlo, statistical).toFixed(1)
}

const getProjections = () => {
  if (!portfolioAnalytics.value?.projections) return {}

  // Return only 1, 3, 5 year projections as requested
  const projections = portfolioAnalytics.value.projections
  return {
    '1_year': projections['1_year'],
    '3_years': projections['3_years'],
    '5_years': projections['5_years']
  }
}

const formatPeriod = (period) => {
  return period.replace('_', ' ').replace(/(\d+)/, '$1').toUpperCase()
}

const formatCurrency = (amount) => {
  if (!amount) return '0'

  // Convert to lakhs/crores for better readability
  if (amount >= 10000000) { // 1 crore
    return `${(amount / 10000000).toFixed(2)} Cr`
  } else if (amount >= 100000) { // 1 lakh
    return `${(amount / 100000).toFixed(2)} L`
  } else {
    return new Intl.NumberFormat('en-IN').format(amount)
  }
}

// Parameter editing methods
const toggleEditMode = (paramType) => {
  isEditingParams.value[paramType] = !isEditingParams.value[paramType]
}

const finishEditing = (paramType) => {
  isEditingParams.value[paramType] = false
}

const hasParameterChanges = computed(() => {
  return (
    editableParams.value.initialInvestment !== originalParams.value.initialInvestment ||
    editableParams.value.goalReturn !== originalParams.value.goalReturn ||
    editableParams.value.investmentHorizon !== originalParams.value.investmentHorizon
  )
})

const rerunAssetAllocation = async () => {
  try {
    // Set full-screen rerunning state to true (hides the entire portfolio display)
    isRerunningFullScreen.value = true
    isRerunning.value = true

    const riskProfile = store.financialStatement?.riskProfile || 'Moderate'

    const payload = {
      riskProfile: riskProfile,
      goal_return: editableParams.value.goalReturn / 100, // Convert percentage to decimal
      goal_return_percentage: editableParams.value.goalReturn, // Store as percentage for retrieval
      investmentHorizon: editableParams.value.investmentHorizon,
      initialInvestment: editableParams.value.initialInvestment
    }

    console.log('Re-running asset allocation with payload:', payload)

    // Call getRiskAssessment method from store
    await store.getRiskAssessment(payload)

    // Reload dashboard data to get updated portfolio
    await store.loadDashboardData(store.user.email)

    // Reload portfolio analytics with new data
    await loadPortfolioAnalytics()

    // Clear cached portfolio analytics data so PortfolioAnalytics.vue fetches fresh data
    // This ensures the updated target allocation (model portfolio) is reflected
    store.portfolioAnalyticsData = null
    console.log('Cleared cached portfolio analytics data to reflect updated target allocation')

    // Clear portfolio attribution cache to reflect latest target asset allocation across portal
    store.portfolioAttributionData = null
    sessionStorage.removeItem('portfolioAttributionData')
    console.log('Cleared portfolio attribution cache to reflect updated target allocation across all pages')

    // Clear localStorage cache used by SectorAttributionOverview.vue
    const portfolioId = store.dashboardData?.portfolioId
    if (portfolioId) {
      const portfolioCacheKey = `portfolio_attribution_data_${portfolioId}`
      localStorage.removeItem(portfolioCacheKey)
      // Also clear the general cache key if it exists
      localStorage.removeItem('portfolio_attribution_data')
      console.log(`Cleared localStorage attribution cache for portfolio ${portfolioId}`)
    }

    // Transform the updated data for display
    if (hasExistingPortfolio.value) {
      transformExistingPortfolioData()
    } else if (riskAssessmentData.value) {
      transformAssessmentData()
    }

    // Update original params to current values
    originalParams.value = { ...editableParams.value }

    // Disable all editing modes
    isEditingParams.value = {
      initialInvestment: false,
      goalReturn: false,
      investmentHorizon: false
    }

    store.fireAlert('Success', 'Asset allocation updated successfully!')

  } catch (error) {
    console.error('Failed to re-run asset allocation:', error)
    store.fireAlert('Error', 'Failed to update asset allocation. Please try again.')
  } finally {
    // Reset both rerunning states
    isRerunningFullScreen.value = false
    isRerunning.value = false
  }
}

// Lifecycle
onMounted(async () => {
  // Only load analytics if user has completed risk assessment or has existing portfolio data
  // This prevents API calls for new customers who haven't completed risk assessment yet
  if (riskAssessmentData.value || hasExistingPortfolio.value) {
    // Load portfolio analytics first
    await loadPortfolioAnalytics()
  } else {
    // For new customers without risk assessment, skip analytics loading
    console.log('Skipping analytics load for new customer - no risk assessment completed yet')
    isLoadingAnalytics.value = false
    portfolioAnalytics.value = null
  }

  // If we already have risk assessment data, transform it
  if (riskAssessmentData.value) {
    transformAssessmentData()
  }
  // If we have existing portfolio data from dashboard, transform and display it
  else if (hasExistingPortfolio.value) {
    transformExistingPortfolioData()
  }
})
</script>

<style scoped>
.model-portfolio-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 16px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.loading-content {
  text-align: center;
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.loading-icon {
  font-size: 3rem;
  color: var(--an-primary);
  margin-bottom: 16px;
}

.questionnaire-container {
  max-width: 1200px;
  margin: 0 auto;
}

.intro-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.intro-section h2 {
  color: var(--an-primary);
  margin-bottom: 12px;
  font-size: 2rem;
}

.intro-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--an-text-secondary);
  margin-bottom: 16px;
}

.user-info-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid var(--an-primary);
}

.user-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
}

.detail-row {
  display: flex;
  gap: 12px;
}

.detail-row .label {
  font-weight: 600;
  color: var(--an-text-primary);
  min-width: 100px;
}

.detail-row .value {
  color: var(--an-text-secondary);
}

.portfolio-results {
  max-width: 1400px;
  margin: 0 auto;
}

.user-profile-summary {
  margin-bottom: 24px;
}

.profile-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.profile-header h3 {
  color: var(--an-primary);
  margin-bottom: 16px;
  font-size: 1.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--an-text-primary);
}

.portfolio-allocation {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.allocation-header {
  text-align: center;
  margin-bottom: 24px;
}

.allocation-header h2 {
  color: var(--an-primary);
  margin-bottom: 8px;
  font-size: 1.8rem;
}

.allocation-subtitle {
  color: var(--an-text-secondary);
  font-size: 1rem;
}

.allocation-display {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chart-analytics-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;
}

.chart-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  min-height: auto;
}

.parameters-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 16px 0;
}

/* Parameters Layout */
.parameters-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.parameter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
}

.parameter-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  flex-shrink: 0;
  min-width: 140px;
}

.parameter-value-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: flex-end;
}

.parameter-input {
  max-width: 150px;
  transition: all 0.3s ease;
}

.parameter-input.editing-field {
  border-color: var(--an-primary) !important;
  background-color: #f0f9ff !important;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1) !important;
}

.parameter-input.editing-field:focus {
  border-color: var(--an-primary) !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2) !important;
}

.edit-btn {
  padding: 6px !important;
  width: 28px !important;
  height: 28px !important;
  flex-shrink: 0;
}

.rerun-section {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.rerun-btn {
  min-width: 200px;
}

/* Analytics Row - Full Width */
.analytics-loading {
  background: white;
  border-radius: 8px;
  padding: 40px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
  text-align: center;
}

.analytics-loading .loading-content {
  background: none;
  box-shadow: none;
  padding: 20px;
}

.analytics-loading .loading-icon {
  font-size: 2.5rem;
  color: var(--an-primary);
  margin-bottom: 16px;
}

.analytics-loading h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.analytics-loading p {
  font-size: 1rem;
  color: var(--an-text-secondary);
  margin: 0;
}

.analytics-row {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 20px;
}

.analytics-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 20px 0;
  text-align: center;
}

/* Metrics Container */
.metrics-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.metric-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-left: 4px solid var(--an-primary);
  transition: transform 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
}

.metric-card.achieved-return {
  border-left-color: #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
}

.metric-card.achieved-return .metric-icon {
  color: #10b981;
}

.metric-icon {
  font-size: 1.5rem;
  color: var(--an-primary);
  width: 32px;
  text-align: center;
  cursor: help;
}

.metric-content {
  flex: 1;
}

.metric-label {
  font-size: 0.8rem;
  color: var(--an-text-secondary);
  font-weight: 500;
  margin-bottom: 4px;
  line-height: 1.2;
}

.metric-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--an-text-primary);
}

/* Analytics Sub-grid */
.analytics-subgrid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
}

.subsection-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
}

/* Projections */
.projections-section {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
}

.projections-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.projection-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
}

.projection-period {
  font-size: 0.8rem;
  opacity: 0.9;
  margin-bottom: 4px;
  font-weight: 500;
}

.projection-amount {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 4px;
}

.projection-gain {
  font-size: 0.8rem;
  opacity: 0.8;
}

/* Risk Assessment */
.risk-assessment {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
}

.risk-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.risk-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
}

.risk-label {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.risk-info {
  font-size: 0.7rem;
  cursor: help;
  opacity: 0.7;
}

.risk-value {
  font-size: 0.9rem;
  color: var(--an-text-primary);
  font-weight: 600;
}

.chart-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  min-height: auto;
}

.chart-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
}

.chart-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin-bottom: 20px;
  margin-top: 0;
  text-align: center;
  width: 100%;
}

.chart-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 40px 20px;
}

.chart-loading-icon {
  font-size: 2rem;
  color: var(--an-primary);
  margin-bottom: 12px;
}

.chart-loading-text {
  font-size: 0.95rem;
  color: var(--an-text-secondary);
  text-align: center;
  margin: 0;
}

.portfolio-chart {
  height: 200px !important;
  width: 200px !important;
  margin: 0 auto 20px auto;
}

.custom-legend {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.legend-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 500px;
  margin: 0 auto;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-text {
  font-size: 0.85rem;
  color: var(--an-text-primary);
}

.legend-label {
  font-weight: 500;
}

.legend-percentage {
  font-weight: 600;
  color: var(--an-primary);
}

.allocation-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.allocation-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  min-height: 180px;
}

.allocation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.allocation-header-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.allocation-name-risk {
  flex: 1;
}

.allocation-name-risk h4 {
  margin: 0 0 6px 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  line-height: 1.2;
}

.risk-badge {
  display: inline-block;
  padding: 3px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.risk-badge-low {
  background: #dbeafe;
  color: #1e40af;
}

.risk-badge-medium {
  background: #fef3c7;
  color: #d97706;
}

.risk-badge-high {
  background: #fee2e2;
  color: #dc2626;
}

.allocation-percentage-large {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--an-primary);
  text-align: center;
  margin: 12px 0;
  line-height: 1;
}

.allocation-description {
  color: var(--an-text-secondary);
  line-height: 1.4;
  font-size: 0.85rem;
  margin-top: auto;
}

/* Risk-based styling */
.risk-low {
  background: #10b981;
  color: #10b981;
}

.risk-medium {
  background: #f59e0b;
  color: #f59e0b;
}

.risk-high {
  background: #ef4444;
  color: #ef4444;
}

.action-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  padding: 32px;
}

.action-btn {
  min-width: 180px;
}

/* Responsive design */
@media (max-width: 1200px) {
  .chart-analytics-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .allocation-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .metrics-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .analytics-subgrid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .projections-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  .detail-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .model-portfolio-container {
    padding: 12px;
  }

  .intro-section,
  .portfolio-allocation,
  .analytics-row {
    padding: 16px;
  }

  .chart-analytics-container {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 20px;
  }

  .chart-section,
  .parameters-section {
    padding: 16px;
  }

  .portfolio-chart {
    height: 180px !important;
    width: 180px !important;
  }

  .legend-grid {
    grid-template-columns: 1fr;
    gap: 8px;
    max-width: 300px;
  }

  /* Parameter rows responsive */
  .parameter-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .parameter-label {
    min-width: auto;
  }

  .parameter-value-group {
    width: 100%;
    justify-content: flex-start;
  }

  .parameter-input {
    max-width: none;
    flex: 1;
  }

  /* Metrics responsive */
  .metrics-container {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .analytics-subgrid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .projections-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .allocation-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 20px;
  }

  .allocation-card {
    padding: 16px;
  }

  .allocation-header-card {
    gap: 12px;
  }

  .allocation-percentage-large {
    font-size: 2.2rem;
  }

  .action-buttons {
    flex-direction: column;
    align-items: center;
    padding: 24px;
    gap: 16px;
  }

  .action-btn {
    width: 100%;
    max-width: 300px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .model-portfolio-container {
    padding: 8px;
  }

  .intro-section,
  .portfolio-allocation,
  .analytics-row {
    padding: 12px;
  }

  .allocation-display {
    gap: 16px;
  }

  .chart-section,
  .parameters-section {
    padding: 12px;
  }

  .chart-title {
    font-size: 1.1rem;
    margin-bottom: 16px;
  }

  .portfolio-chart {
    height: 160px !important;
    width: 160px !important;
    margin-bottom: 16px;
  }

  .custom-legend {
    margin-bottom: 16px;
  }

  .legend-grid {
    gap: 6px;
    max-width: 280px;
  }

  .legend-text {
    font-size: 0.8rem;
  }

  /* Compact parameter layout for mobile */
  .parameter-row {
    padding: 6px 0;
  }

  .parameter-label {
    font-size: 0.8rem;
  }

  .parameter-input {
    font-size: 0.9rem;
  }

  .edit-btn {
    width: 24px !important;
    height: 24px !important;
    padding: 4px !important;
  }

  .rerun-btn {
    min-width: auto;
    width: 100%;
    font-size: 0.9rem;
  }

  /* Analytics compact */
  .analytics-title {
    font-size: 1.1rem;
    margin-bottom: 16px;
  }

  .metrics-container {
    gap: 8px;
  }

  .metric-card {
    padding: 12px;
  }

  .metric-icon {
    font-size: 1.2rem;
    width: 28px;
  }

  .metric-label {
    font-size: 0.75rem;
  }

  .metric-value {
    font-size: 1rem;
  }

  .projections-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .projection-card {
    padding: 10px;
  }

  .subsection-title {
    font-size: 0.9rem;
  }

  .allocation-grid {
    gap: 12px;
    margin-top: 16px;
  }

  .allocation-card {
    padding: 12px;
    min-height: 160px;
  }

  .allocation-header-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 10px;
  }

  .allocation-percentage-large {
    font-size: 2rem;
    margin: 10px 0;
  }

  .allocation-name-risk h4 {
    font-size: 0.95rem;
    margin-bottom: 4px;
  }

  .allocation-description {
    font-size: 0.8rem;
    margin-top: 8px;
  }

  .action-btn {
    min-width: unset;
    width: 100%;
  }

  .intro-section h2 {
    font-size: 1.5rem;
  }

  .intro-text {
    font-size: 1rem;
  }

  .user-info-card {
    padding: 16px;
  }

  .profile-card {
    padding: 16px;
  }

  .profile-header h3 {
    font-size: 1.1rem;
  }

  .detail-label {
    font-size: 0.8rem;
  }

  .detail-value {
    font-size: 1rem;
  }
}

@media (max-width: 768px) {
  .model-portfolio-container {
    padding: 12px;
  }

  .intro-section,
  .portfolio-allocation {
    padding: 20px;
  }

  .allocation-display {
    gap: 20px;
  }

  .chart-section {
    padding: 16px;
  }

  .portfolio-chart {
    height: 180px !important;
    width: 180px !important;
  }

  .legend-grid {
    grid-template-columns: 1fr;
    gap: 8px;
    max-width: 300px;
  }

  .legend-item {
    justify-content: flex-start;
  }

  .allocation-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 20px;
  }

  .allocation-card {
    padding: 16px;
  }

  .allocation-header-card {
    gap: 12px;
  }

  .allocation-percentage-large {
    font-size: 2.2rem;
  }

  .allocation-name-risk h4 {
    font-size: 1rem;
  }

  .action-buttons {
    flex-direction: column;
    align-items: center;
    padding: 24px;
    gap: 16px;
  }

  .action-btn {
    width: 100%;
    max-width: 300px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .user-details {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .profile-card {
    padding: 24px;
  }

  .profile-header h3 {
    font-size: 1.3rem;
    margin-bottom: 16px;
  }
}

/* Large screens */
@media (min-width: 1400px) {
  .model-portfolio-container {
    padding: 32px;
  }

  .portfolio-results {
    max-width: 1600px;
  }

  .allocation-display {
    gap: 40px;
  }

  .chart-section {
    padding: 24px;
  }
}
</style>
